import { LoadingGuard } from '@lifebit-ai/lds'
import { FileTypes } from 'enums'
import React from 'react'

import { DocxViewer } from './DocxViewer'
import { IFrameViewer } from './IFrameViewer'
import { ImageViewer } from './ImageViewer'
import { NoContent } from './NoContent'
import { SVGViewer } from './SVGViewer'
import { TextFileViewer } from './TextFileViewer'
import { XLSViewer } from './XLSViewer'
import { FileContentProps, useFileContent } from './hooks/useFileContent'
import { ERROR_LOADING_FILE_CONTENT_MESSAGE } from './utils'

const { Loading, Error } = LoadingGuard

export const SVG_FILE_PREVIEW_TEST_ID = 'svg-file-preview'
export const DOCX_FILE_PREVIEW_TEST_ID = 'docx-file-preview'

type SignedUrlPreviewProps = {
  isIFrameExtension: boolean
  isRasterImageFileExtension: boolean
  isSVGFileExtension: boolean
  isSignedUrlBinaryViewer: boolean
  isDocxFileExtension: boolean
  fileExtension: string
  signedUrl?: string
  signedUrlBinaryQuery: ReturnType<typeof useFileContent>['signedUrlBinaryQuery']
}

/**
 * Used to preview data via a signed URL.
 */
const SignedUrlPreview = ({
  isIFrameExtension,
  isRasterImageFileExtension,
  isSVGFileExtension,
  isSignedUrlBinaryViewer,
  isDocxFileExtension,
  fileExtension,
  signedUrl,
  signedUrlBinaryQuery
}: SignedUrlPreviewProps) => {
  if (isRasterImageFileExtension || isSVGFileExtension) {
    return <ImageViewer fileExtension={fileExtension} signedUrl={signedUrl} />
  }

  if (isIFrameExtension) {
    return <IFrameViewer fileExtension={fileExtension} signedUrl={signedUrl} />
  }

  if (isSignedUrlBinaryViewer) {
    if (signedUrlBinaryQuery.isLoading) return <Loading />
    if (signedUrlBinaryQuery.isError) return <Error title={ERROR_LOADING_FILE_CONTENT_MESSAGE} />
    if (!signedUrlBinaryQuery.data?.fileContent) return <NoContent />
    if (isDocxFileExtension) return <DocxViewer data={signedUrlBinaryQuery.data} />

    return <XLSViewer data={signedUrlBinaryQuery.data} />
  }

  return <TextFileViewer fileExtension={fileExtension || FileTypes.TXT} signedUrl={signedUrl} />
}

type FileContentPreviewProps = {
  isIFrameExtension: boolean
  isRasterImageFileExtension: boolean
  isDocxFileExtension: boolean
  isXLSFileExtension: boolean
  isSVGFileExtension: boolean
  fileExtension: string
  textFileContentQuery: ReturnType<typeof useFileContent>['textFileContentQuery']
  binaryFileContentQuery: ReturnType<typeof useFileContent>['binaryFileContentQuery']
}

/**
 * Used to preview data that has already been downloaded.
 */
const FileContentPreview = ({
  isIFrameExtension,
  isRasterImageFileExtension,
  isDocxFileExtension,
  isXLSFileExtension,
  isSVGFileExtension,
  fileExtension,
  textFileContentQuery,
  binaryFileContentQuery
}: FileContentPreviewProps) => {
  const textFileContentData = textFileContentQuery.data
  const binaryFileContentData = binaryFileContentQuery.data

  if (textFileContentQuery.isLoading || binaryFileContentQuery.isLoading) {
    return <Loading />
  }

  if (textFileContentQuery.isError || binaryFileContentQuery.isError) {
    return <Error title={ERROR_LOADING_FILE_CONTENT_MESSAGE} />
  }

  const hasNoContent =
    (textFileContentQuery.isSuccess && !textFileContentData?.fileContent && !textFileContentData?.signedUrl) ||
    (binaryFileContentQuery.isSuccess && !binaryFileContentData?.fileContent && !binaryFileContentData?.signedUrl)
  if (hasNoContent) {
    return <NoContent />
  }

  if (isDocxFileExtension) return <DocxViewer data={binaryFileContentData} />
  if (isXLSFileExtension) return <XLSViewer data={binaryFileContentData} />
  if (isSVGFileExtension) return <SVGViewer data={textFileContentData?.fileContent} />

  if (isRasterImageFileExtension) {
    const signedUrl = textFileContentData?.signedUrl || binaryFileContentData?.signedUrl
    return <ImageViewer fileExtension={fileExtension} signedUrl={signedUrl} />
  }

  if (isIFrameExtension) {
    const signedUrl = textFileContentData?.signedUrl || binaryFileContentData?.signedUrl
    return <IFrameViewer fileExtension={fileExtension} signedUrl={signedUrl} />
  }

  // Lazy Log Viewer previews remaining types; unsupported ones hit the ErrorBoundary
  return <TextFileViewer fileExtension={fileExtension} signedUrl={textFileContentData?.signedUrl} />
}

const FileContent = (props: FileContentProps) => {
  const {
    isSignedUrlPreview,
    signedUrl,
    fileExtension,
    isValidImageFileExtension,
    isHTMLFileExtension,
    isDocxFileExtension,
    isPDFFileExtension,
    isSVGFileExtension,
    isXLSFileExtension,
    isSignedUrlBinaryViewer,
    textFileContentQuery,
    binaryFileContentQuery,
    signedUrlBinaryQuery
  } = useFileContent(props)

  const isRasterImageFileExtension = isValidImageFileExtension && !isSVGFileExtension
  const isIFrameExtension = isHTMLFileExtension || isPDFFileExtension

  if (isSignedUrlPreview) {
    return (
      <SignedUrlPreview
        isIFrameExtension={isIFrameExtension}
        isRasterImageFileExtension={isRasterImageFileExtension}
        isSVGFileExtension={isSVGFileExtension}
        isSignedUrlBinaryViewer={isSignedUrlBinaryViewer}
        isDocxFileExtension={isDocxFileExtension}
        fileExtension={fileExtension}
        signedUrl={signedUrl}
        signedUrlBinaryQuery={signedUrlBinaryQuery}
      />
    )
  }

  return (
    <FileContentPreview
      isIFrameExtension={isIFrameExtension}
      isRasterImageFileExtension={isRasterImageFileExtension}
      isDocxFileExtension={isDocxFileExtension}
      isXLSFileExtension={isXLSFileExtension}
      isSVGFileExtension={isSVGFileExtension}
      fileExtension={fileExtension}
      textFileContentQuery={textFileContentQuery}
      binaryFileContentQuery={binaryFileContentQuery}
    />
  )
}

export default FileContent
